import { LocationProviderService } from '@addins/core/core';
import { Injectable } from '@angular/core';
import { Geoposition } from '@awesome-cordova-plugins/geolocation/ngx';
import { Coordinate } from '@models/coordinate';
import { Guid } from '@models/guid';
import { ResourcePosition } from '@models/imported/SagaSchema/ResourcePosition';
import { transform } from '@services/map';
import { MyEquipmentService } from '@techwan/ionic-core';
import Sphere from 'ol/sphere';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { ProjectionConverterService } from '../projection-converter/projection-converter.service';

@Injectable()
export class MyLocationService {
  private readonly EARTH_RADIUS: number = 6371000;
  readonly WSG84_EPSG: string = 'EPSG:4326';

  private _empty: Coordinate = { x: 0, y: 0, epsg: this.WSG84_EPSG };
  get empty(): Coordinate {
    return this._empty;
  }

  private _defaultCoordinate: Coordinate = this.empty;
  get defaultCoordinate(): Coordinate {
    return this._defaultCoordinate;
  }

  // the new location converted to our map's projection
  private _onNewLocation = new BehaviorSubject<Coordinate>(this.empty);
  get value(): Coordinate {
    return this._onNewLocation.value;
  }

  get $change(): Observable<Coordinate> {
    return this._onNewLocation.asObservable();
  }

  private _newLocationSub: Subscription = null;

  // same as new location, but as Geoposition
  private _mostRecent: Geoposition = null;
  get mostRecent(): Geoposition {
    return this._mostRecent;
  }

  constructor(
    private myEquipment: MyEquipmentService,
    private locationProvider: LocationProviderService,
    private projectionConverter: ProjectionConverterService
  ) {}

  /**
   * Convert the current position to the given view's projection.
   * @param view Target view on which the position should be shown.
   */
  forView(view: ol.View): ol.Coordinate {
    const pos = this.value;
    return this.projectionConverter.convertToView(view, [pos.x, pos.y], this.projectionConverter.getProjection(pos.epsg));
  }

  getCurrent(): Promise<Coordinate> {
    return this.value !== this.empty
      ? Promise.resolve(this.value)
      : this.locationProvider.getCurrent().then(position => this.setNewLocation(position));
  }

  private setNewLocation(position: Geoposition = null): Coordinate {
    const coordinate: Coordinate =
      position !== null ? { x: position.coords.longitude, y: position.coords.latitude, epsg: this.WSG84_EPSG } : this.empty;

    this._mostRecent = position;
    this._onNewLocation.next(coordinate);

    return coordinate;
  }

  start(): Observable<void> {
    if (this._newLocationSub !== null) {
      return of(null);
    }

    let uid: Guid;
    const myDevice = this.myEquipment.myDevice;
    if (myDevice) {
      uid = myDevice.ObjGuid;
      const resourcePositions: ResourcePosition[] = myDevice.component<ResourcePosition>(ResourcePosition);
      if (resourcePositions.length) {
        this.setDefaultCoordinate(resourcePositions[0]);
      }
    }

    this._newLocationSub = this.locationProvider.$location.subscribe(position => this.setNewLocation(position));
    return this.locationProvider.start(uid);
  }

  private setDefaultCoordinate(resourcePosition: ResourcePosition) {
    this._defaultCoordinate = transform(
      {
        x: resourcePosition.MapX,
        y: resourcePosition.MapY,
        epsg: resourcePosition.CoordEpsgCode
      },
      this.WSG84_EPSG
    );
  }

  stop(): Promise<void> {
    this._newLocationSub?.unsubscribe();
    this._newLocationSub = null;
    return this.locationProvider.stop();
  }

  /**
   * Returns the distance in meters between two coordinates using the haversine formula.
   * @param coord1 first coordinate
   * @param coord2 second coordinate, defaults to my units current position
   */
  haversineDistance(coord1: Coordinate, coord2: Coordinate = this.value): number {
    const coord1Normalized: Coordinate = transform(coord1, this.WSG84_EPSG);
    const coord2Normalized: Coordinate = transform(coord2, this.WSG84_EPSG);

    const olFrom: ol.Coordinate = [coord1Normalized.x, coord1Normalized.y];
    const olTo: ol.Coordinate = [coord2Normalized.x, coord2Normalized.y];

    const wgs84Sphere: Sphere = new Sphere(this.EARTH_RADIUS);
    return wgs84Sphere.haversineDistance(olFrom, olTo);
  }
}
